import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  Frame,
  KernelEvent,
  LoadRequest,
  LoadResult,
  LocalHost,
  RemoteInput,
  ReplayLoadRequest,
  ReplayLoadResult,
} from "@picnic-field/local-host/protocol";
import { LatencyProbe } from "../../remote";
import { courseDeps } from "../lib/deps";
import type { SceneRun } from "../components/CourseScene";
import type { HostStatus } from "../types";

/**
 * 커널 호스트(C-05) 한 개의 수명 — 전시관(FS-03-01) · 친구 놀이(FS-03-03) · 리플레이 뷰어(FS-14-01)가 쓴다.
 * (코스 한 판은 판 상태가 많아 {@link ./useCourseRun} 이 따로 가진다.) 프레임은 ref 로만 흘리고 React 상태는 load 결과뿐.
 */
export interface HostHandle extends SceneRun {
  status: HostStatus;
  load: LoadResult | null;
  loadWorld(req: LoadRequest): Promise<LoadResult | null>;
  loadReplay(req: ReplayLoadRequest): Promise<ReplayLoadResult | null>;
  host(): LocalHost | null;
}

export function useHost(onEvent?: (e: KernelEvent) => void): HostHandle {
  const [status, setStatus] = useState<HostStatus>("creating");
  const [load, setLoad] = useState<LoadResult | null>(null);
  const hostRef = useRef<LocalHost | null>(null);
  const latest = useRef<Frame | null>(null);
  const frameCbs = useRef(new Set<(f: Frame) => void>());
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const probe = useRef(new LatencyProbe()).current;
  const seq = useRef(0);

  useEffect(() => {
    let alive = true;
    let host: LocalHost | null = null;
    const offs: (() => void)[] = [];
    courseDeps()
      .createHost()
      .then((h) => {
        if (!alive) return h.dispose();
        host = h;
        hostRef.current = h;
        offs.push(
          h.onFrame((f) => {
            latest.current = f;
            probe.markFrame(performance.now());
            frameCbs.current.forEach((cb) => cb(f));
          }),
          h.onEvent((e) => onEventRef.current?.(e)),
        );
        setStatus("idle");
      })
      .catch(() => alive && setStatus("error"));
    return () => {
      alive = false;
      offs.forEach((o) => o());
      host?.dispose();
      hostRef.current = null;
    };
  }, [probe]);

  const loadWorld = useCallback(async (req: LoadRequest) => {
    const h = hostRef.current;
    if (!h) return null;
    const n = ++seq.current;
    latest.current = null;
    setStatus("loading");
    try {
      const r = await h.load(req);
      if (n !== seq.current) return null;
      setLoad(r);
      setStatus("ready");
      return r;
    } catch {
      if (n === seq.current) setStatus("error");
      return null;
    }
  }, []);

  const loadReplay = useCallback(async (req: ReplayLoadRequest) => {
    const h = hostRef.current;
    if (!h) return null;
    const n = ++seq.current;
    latest.current = null;
    setStatus("loading");
    try {
      const r = await h.loadReplay(req);
      if (n !== seq.current) return null;
      setLoad(r);
      setStatus("ready");
      return r;
    } catch {
      if (n === seq.current) setStatus("error");
      return null;
    }
  }, []);

  return useMemo(
    () => ({
      status,
      load,
      loadWorld,
      loadReplay,
      host: () => hostRef.current,
      latestFrame: () => latest.current,
      onFrame: (cb: (f: Frame) => void) => {
        frameCbs.current.add(cb);
        return () => void frameCbs.current.delete(cb);
      },
      probe,
    }),
    [status, load, loadWorld, loadReplay, probe],
  );
}

/** 리모컨 입력을 호스트로(지연 계측 포함) */
export function sendInput(h: HostHandle, s: RemoteInput) {
  h.probe.markInput(performance.now());
  h.host()?.input(s);
}
